import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Edge fades for a horizontal scroller (tab strips, chip rows, wide tables): "there is more
 * this way" instead of "this is cut off". Attach `ref` to the scrolling element and add
 * `fade` to its className — `fade-x-start` / `fade-x-end` / `fade-x-both` (styles/ui.css)
 * appear only while content is clipped on that side.
 *
 *   const { ref, fade } = useScrollFade();
 *   <div ref={ref} className={`row ${fade}`}>…</div>
 *
 * `activeSelector` (optional): the child that counts as active; whenever `activeKey` changes
 * it is scrolled into view inside the row (the page itself never scrolls). `watch`: any value
 * that changes when the row's children do (the tab count), so their sizes are observed again.
 */
export const DEFAULT_ACTIVE_SELECTOR =
  '[aria-selected="true"], [aria-current="page"], [aria-current="true"], [aria-pressed="true"], [data-active="true"], .active';

function reducedMotion() {
  try {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

export function useScrollFade({ activeKey, activeSelector = DEFAULT_ACTIVE_SELECTOR, watch } = {}) {
  const ref = useRef(null);
  const [edges, setEdges] = useState({ start: false, end: false });

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const start = el.scrollLeft > 2;
    const end = max > 2 && el.scrollLeft < max - 2;
    setEdges((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    measure();
    el.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    // ResizeObserver is missing in jsdom and very old browsers; the scroll + resize listeners
    // alone still keep the fades right once the user scrolls.
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) {
      ro.observe(el);
      Array.from(el.children).forEach((child) => ro.observe(child));
    }
    return () => {
      el.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
      if (ro) ro.disconnect();
    };
  }, [measure, watch]);

  // Keep the active item visible inside the row.
  useEffect(() => {
    const el = ref.current;
    if (!el || activeKey === undefined) return;
    const item = el.querySelector(activeSelector);
    if (!item || typeof el.scrollTo !== 'function') return;
    const rowBox = el.getBoundingClientRect();
    const box = item.getBoundingClientRect();
    const pad = 24;
    const behavior = reducedMotion() ? 'auto' : 'smooth';
    if (box.left < rowBox.left + pad) {
      el.scrollTo({ left: el.scrollLeft + box.left - rowBox.left - pad, behavior });
    } else if (box.right > rowBox.right - pad) {
      el.scrollTo({ left: el.scrollLeft + box.right - rowBox.right + pad, behavior });
    }
  }, [activeKey, activeSelector]);

  const fade = edges.start && edges.end ? 'fade-x-both' : edges.start ? 'fade-x-start' : edges.end ? 'fade-x-end' : '';
  return { ref, fade };
}

export default useScrollFade;
